/**
 * Feedback API functions
 * Used by the owner-facing Feedback admin dashboard to review and reply to
 * user-submitted feedback threads (two-way support-thread system).
 */

import apiClient from "../../services/apiClient";
import { dataLoaderUri } from "../API";

// Types shared with the Feedback dashboard

export type FeedbackType = "general" | "skip";
export type FeedbackStatus = "new" | "reviewed" | "resolved";
export type MessageAuthor = "user" | "owner";

export interface FeedbackMessage {
  messageId: string;
  author: MessageAuthor;
  authorName: string;
  text: string;
  createdAt: string;
}

export interface FeedbackContext {
  screen?: string;
  exerciseType?: string;
  exerciseName?: string;
  sectionId?: string;
  sectionName?: string;
  wordId?: string;
  targetLanguage?: string;
  baseLanguage?: string;
  appVersion?: string;
  device?: string;
  [key: string]: unknown;
}

export interface FeedbackThread {
  feedbackId: string;
  userEmail: string;
  userName: string;
  type: FeedbackType;
  category: string;
  status: FeedbackStatus;
  context: FeedbackContext;
  messages: FeedbackMessage[];
  createdAt: string;
  updatedAt: string;
  ownerHasUnread?: boolean;
}

export interface FetchFeedbackParams {
  status?: FeedbackStatus;
  awaitingReply?: boolean;
  cursor?: string | null;
  limit?: number;
}

export interface FetchFeedbackResponse {
  threads: FeedbackThread[];
  nextCursor: string | null;
  count: number;
}

export interface FetchFeedbackThreadResponse {
  thread: FeedbackThread;
}

export interface ReplyToFeedbackResponse {
  status: "sent";
  message: FeedbackMessage;
}

export interface UpdateFeedbackStatusResponse {
  status: FeedbackStatus;
}

// Retry configuration (mirrors AdminAPI.tsx / API.tsx conventions)
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  retryableStatusCodes: [503, 502, 504, 429],
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function withRetry<T>(apiCall: () => Promise<T>, operation: string = "API call"): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      const result = await apiCall();
      if (attempt > 0) {
        console.log(`✅ ${operation} succeeded on attempt ${attempt + 1}`);
      }
      return result;
    } catch (error: any) {
      lastError = error;

      let isRetryable = false;
      let statusCode = 0;

      if (error.response) {
        statusCode = error.response.status;
        isRetryable = RETRY_CONFIG.retryableStatusCodes.includes(statusCode);
      } else if (!error.response && error.message) {
        // Network error
        isRetryable = true;
      }

      if (!isRetryable || attempt === RETRY_CONFIG.maxRetries) {
        break;
      }

      const delay = Math.min(RETRY_CONFIG.baseDelay * Math.pow(2, attempt), RETRY_CONFIG.maxDelay);

      console.warn(`⚠️ ${operation} failed on attempt ${attempt + 1}. Retrying in ${delay}ms...`);
      await sleep(delay);
    }
  }

  console.error(`❌ ${operation} failed after ${RETRY_CONFIG.maxRetries + 1} attempts`);
  throw lastError;
}

/**
 * List feedback threads, paginated via opaque cursor.
 * Pass `status` for a status-filtered queue, or `awaitingReply: true` for the
 * "awaiting reply" queue (threads where ownerHasUnread is true).
 */
export async function fetchFeedback(
  params: FetchFeedbackParams = {}
): Promise<FetchFeedbackResponse> {
  const { status, awaitingReply, cursor, limit = 25 } = params;

  const queryParams: Record<string, string> = { limit: String(limit) };
  if (status) {
    queryParams.status = status;
  }
  if (awaitingReply) {
    queryParams.awaitingReply = "true";
  }
  if (cursor) {
    queryParams.cursor = cursor;
  }

  return withRetry(async () => {
    const response = await apiClient.get<FetchFeedbackResponse>(
      `${dataLoaderUri}/api/v1/feedback`,
      { params: queryParams }
    );
    return response;
  }, "fetchFeedback");
}

/**
 * Read a single feedback thread (full message transcript).
 */
export async function fetchFeedbackThread(
  feedbackId: string,
  userEmail: string
): Promise<FeedbackThread> {
  return withRetry(async () => {
    const response = await apiClient.get<FetchFeedbackThreadResponse>(
      `${dataLoaderUri}/api/v1/feedback`,
      { params: { feedbackId, userEmail } }
    );
    return response.thread;
  }, `fetchFeedbackThread(${feedbackId})`);
}

/**
 * Send an owner reply on a feedback thread.
 */
export async function replyToFeedback(
  feedbackId: string,
  userEmail: string,
  text: string
): Promise<FeedbackMessage> {
  return withRetry(async () => {
    const response = await apiClient.put<ReplyToFeedbackResponse>(
      `${dataLoaderUri}/api/v1/feedback`,
      {
        action: "reply",
        feedbackId,
        userEmail,
        text,
      }
    );
    return response.message;
  }, `replyToFeedback(${feedbackId})`);
}

/**
 * Change a feedback thread's status (new / reviewed / resolved).
 */
export async function updateFeedbackStatus(
  feedbackId: string,
  userEmail: string,
  status: FeedbackStatus
): Promise<FeedbackStatus> {
  return withRetry(async () => {
    const response = await apiClient.put<UpdateFeedbackStatusResponse>(
      `${dataLoaderUri}/api/v1/feedback`,
      {
        action: "status",
        feedbackId,
        userEmail,
        status,
      }
    );
    return response.status;
  }, `updateFeedbackStatus(${feedbackId})`);
}
